import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { generateSlug } from "@/lib/utils";
import { Plus, Edit, Trash2, Star, Eye, EyeOff } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { PackageManager } from "@/components/PackageManager";
import { ImageUploader } from "@/components/admin/ImageUploader";

const tourSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  slug: z.string().min(1, "El slug es requerido"),
  main_image_url: z.string().url("Debe ser una URL válida").optional().or(z.literal("")),
  gallery_images_urls: z.string().optional(),
  long_description: z.string().optional(),
  short_description: z.string().optional(),
  category_id: z.string().min(1, "La categoría es requerida"),
  location: z.string().optional(),
  duration: z.string().optional(),
  max_capacity: z.number().min(1, "La capacidad debe ser mayor a 0").optional(),
  features: z.string().optional(),
  highlights: z.string().optional(),
  not_suitable_for: z.string().optional(),
  includes: z.string().optional(),
  excludes: z.string().optional(),
  pricing_type: z.enum(['individual', 'package']),
  adult_price: z.number().min(0, "El precio debe ser mayor o igual a 0").optional(),
  child_price: z.number().min(0, "El precio debe ser mayor o igual a 0").optional(),
  base_price: z.number().min(0, "El precio base debe ser mayor o igual a 0").optional(),
  schedules: z.string().optional(),
  is_active: z.boolean(),
  is_featured: z.boolean(),
});

type TourFormData = z.infer<typeof tourSchema>;

interface Tour {
  id: string;
  name: string;
  slug: string;
  main_image_url: string | null;
  gallery_images_urls: string[] | null;
  long_description: string | null;
  short_description: string | null;
  category_id: string | null;
  location: string | null;
  duration: string | null;
  max_capacity: number | null;
  features: string[] | null;
  highlights: string[] | null;
  not_suitable_for: string[] | null;
  includes: string[] | null;
  excludes: string[] | null;
  pricing_type: 'individual' | 'package';
  adult_price: number | null;
  child_price: number | null;
  base_price: number | null;
  schedules: string[] | null;
  is_active: boolean;
  is_featured: boolean;
  tour_categories?: {
    name: string;
  } | null;
  tour_packages?: any[] | null;
}

interface TourCategory {
  id: string;
  name: string;
}

const AdminTours = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [categories, setCategories] = useState<TourCategory[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTour, setEditingTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const form = useForm<TourFormData>({
    resolver: zodResolver(tourSchema),
    defaultValues: {
      name: "",
      slug: "",
      main_image_url: "",
      gallery_images_urls: "",
      long_description: "",
      short_description: "",
      category_id: "",
      location: "",
      duration: "",
      max_capacity: 1,
      features: "",
      highlights: "",
      not_suitable_for: "",
      includes: "",
      excludes: "",
      pricing_type: "individual",
      adult_price: 0,
      child_price: 0,
      base_price: 0,
      schedules: "",
      is_active: true,
      is_featured: false,
    },
  });

  useEffect(() => {
    fetchTours();
    fetchCategories();
  }, []);

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select(`
          *,
          tour_categories (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTours((data || []).map(tour => ({
        ...tour,
        pricing_type: (tour.pricing_type as 'individual' | 'package') || 'individual'
      })));
    } catch (error) {
      console.error('Error fetching tours:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los tours",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('tour_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const onSubmit = async (data: TourFormData) => {
    try {
      const tourData = {
        name: data.name,
        slug: data.slug,
        main_image_url: data.main_image_url || null,
        gallery_images_urls: data.gallery_images_urls ? data.gallery_images_urls.split('\n').filter(url => url.trim()) : [],
        long_description: data.long_description || null,
        short_description: data.short_description || null,
        category_id: data.category_id,
        location: data.location || null,
        duration: data.duration || null,
        max_capacity: data.max_capacity || null,
        features: data.features ? data.features.split('\n').filter(item => item.trim()) : [],
        highlights: data.highlights ? data.highlights.split('\n').filter(item => item.trim()) : [],
        not_suitable_for: data.not_suitable_for ? data.not_suitable_for.split('\n').filter(item => item.trim()) : [],
        includes: data.includes ? data.includes.split('\n').filter(item => item.trim()) : [],
        excludes: data.excludes ? data.excludes.split('\n').filter(item => item.trim()) : [],
        pricing_type: data.pricing_type,
        adult_price: data.adult_price || null,
        child_price: data.child_price || null,
        base_price: data.base_price || null,
        schedules: data.schedules ? data.schedules.split('\n').filter(schedule => schedule.trim()) : [],
        is_active: data.is_active,
        is_featured: data.is_featured,
      };

      if (editingTour) {
        const { error } = await supabase
          .from('tours')
          .update(tourData)
          .eq('id', editingTour.id);

        if (error) throw error;

        toast({
          title: "Éxito",
          description: "Tour actualizado correctamente",
        });
      } else {
        const { error } = await supabase
          .from('tours')
          .insert([tourData]);

        if (error) throw error;

        toast({
          title: "Éxito",
          description: "Tour creado correctamente",
        });
      }

      setIsDialogOpen(false);
      setEditingTour(null);
      form.reset();
      fetchTours();
    } catch (error) {
      console.error('Error saving tour:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el tour",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (tour: Tour) => {
    setEditingTour(tour);
    form.reset({
      name: tour.name,
      slug: tour.slug,
      main_image_url: tour.main_image_url || "",
      gallery_images_urls: tour.gallery_images_urls?.join('\n') || "",
      long_description: tour.long_description || "",
      short_description: tour.short_description || "",
      category_id: tour.category_id || "",
      location: tour.location || "",
      duration: tour.duration || "",
      max_capacity: tour.max_capacity || 1,
      features: tour.features?.join('\n') || "",
      highlights: tour.highlights?.join('\n') || "",
      not_suitable_for: tour.not_suitable_for?.join('\n') || "",
      includes: tour.includes?.join('\n') || "",
      excludes: tour.excludes?.join('\n') || "",
      pricing_type: tour.pricing_type || "individual",
      adult_price: tour.adult_price || 0,
      child_price: tour.child_price || 0,
      base_price: tour.base_price || 0,
      schedules: tour.schedules?.join('\n') || "",
      is_active: tour.is_active,
      is_featured: tour.is_featured,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este tour?')) return;

    try {
      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Tour eliminado correctamente",
      });

      fetchTours();
    } catch (error) {
      console.error('Error deleting tour:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el tour",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (tour: Tour) => {
    try {
      const { error } = await supabase
        .from('tours')
        .update({ is_active: !tour.is_active })
        .eq('id', tour.id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: `Tour ${!tour.is_active ? 'activado' : 'desactivado'} correctamente`,
      });

      fetchTours();
    } catch (error) {
      console.error('Error toggling tour status:', error);
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado del tour",
        variant: "destructive",
      });
    }
  };

  const handleToggleFeatured = async (tour: Tour) => {
    try {
      const { error } = await supabase
        .from('tours')
        .update({ is_featured: !tour.is_featured })
        .eq('id', tour.id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: `Tour ${!tour.is_featured ? 'destacado' : 'no destacado'} correctamente`,
      });

      fetchTours();
    } catch (error) {
      console.error('Error toggling tour featured status:', error);
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado destacado del tour",
        variant: "destructive",
      });
    }
  };

  const handleNameChange = (name: string) => {
    form.setValue('name', name);
    form.setValue('slug', generateSlug(name));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Gestión de Tours</h1>
            <p className="text-muted-foreground">
              Crea y administra los tours disponibles
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingTour(null);
                form.reset();
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo Tour
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTour ? 'Editar Tour' : 'Crear Nuevo Tour'}
                </DialogTitle>
                <DialogDescription>
                  {editingTour 
                    ? 'Modifica los datos del tour' 
                    : 'Completa la información para crear un nuevo tour'}
                </DialogDescription>
              </DialogHeader>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nombre del Tour</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Ej: Tour a Saona Island"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                handleNameChange(e.target.value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Slug (URL)</FormLabel>
                          <FormControl>
                            <Input placeholder="tour-a-saona-island" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoría</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona una categoría" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ubicación</FormLabel>
                          <FormControl>
                            <Input placeholder="Punta Cana" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duración</FormLabel>
                          <FormControl>
                            <Input placeholder="8 horas" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="max_capacity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Capacidad Máxima</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="20"
                              {...field}
                              onChange={(e) => field.onChange(+e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="main_image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Imagen Principal</FormLabel>
                        <FormControl>
                          <ImageUploader
                            label=""
                            value={field.value || ''}
                            onChange={field.onChange}
                            folder="tours"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gallery_images_urls"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Galería de Imágenes (URLs, una por línea)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="https://example.com/image1.jpg
https://example.com/image2.jpg"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="short_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Descripción Corta</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Breve descripción del tour..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="long_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Descripción Larga</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Descripción detallada del tour..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="features"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Características (una por línea)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Transporte incluido
Guía bilingüe"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="highlights"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Highlights (una por línea)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Playa paradisíaca
Snorkel en aguas cristalinas
Comida local auténtica"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="includes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Qué Incluye (una por línea)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Almuerzo
Bebidas
Equipo de snorkel"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="excludes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>No Incluye (una por línea)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Propinas
Gastos personales"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="not_suitable_for"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>No Apto Para (una por línea)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Personas con movilidad reducida
Niños menores de 5 años
Mujeres embarazadas"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Pricing Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Configuración de Precios</h3>
                    
                    <FormField
                      control={form.control}
                      name="pricing_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo de Precio</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona el tipo de precio" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="individual">Individual (por persona)</SelectItem>
                              <SelectItem value="package">Paquetes</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {form.watch("pricing_type") === "individual" ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="adult_price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Precio Adulto (USD)</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="89.99"
                                  {...field}
                                  onChange={(e) => field.onChange(+e.target.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="child_price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Precio Niño (USD)</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="45.99"
                                  {...field}
                                  onChange={(e) => field.onChange(+e.target.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="base_price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Precio Base (USD) - Para mostrar "from $X"</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="80.00"
                                  {...field}
                                  onChange={(e) => field.onChange(+e.target.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        {editingTour && (
                          <PackageManager 
                            tourId={editingTour.id}
                            onMinPriceChange={(minPrice) => {
                              form.setValue("base_price", minPrice);
                            }}
                          />
                        )}
                        
                        {!editingTour && (
                          <div className="bg-muted/50 p-4 rounded-lg text-center text-muted-foreground">
                            Primero guarda el tour para poder gestionar los paquetes
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <Separator />

                  <FormField
                    control={form.control}
                    name="schedules"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Horarios (uno por línea)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="7:00 AM - 3:00 PM
9:00 AM - 5:00 PM"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <div className="flex items-center space-x-8">
                    <FormField
                      control={form.control}
                      name="is_active"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Tour Activo</FormLabel>
                            <div className="text-sm text-muted-foreground">
                              El tour será visible para los usuarios
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="is_featured"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Tour Destacado</FormLabel>
                            <div className="text-sm text-muted-foreground">
                              El tour aparecerá como destacado
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit">
                      {editingTour ? 'Actualizar' : 'Crear'} Tour
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Cargando tours...</div>
          </div>
        ) : (
          <div className="grid gap-6">
            {tours.length === 0 ? (
              <Card>
                <CardContent className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <h3 className="font-medium">No hay tours creados</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Crea tu primer tour para empezar
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              tours.map((tour) => (
                <Card key={tour.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">{tour.name}</CardTitle>
                          {tour.is_featured && (
                            <Badge variant="default" className="bg-yellow-500">
                              <Star className="h-3 w-3 mr-1" />
                              Destacado
                            </Badge>
                          )}
                          <Badge variant={tour.is_active ? "default" : "secondary"}>
                            {tour.is_active ? (
                              <>
                                <Eye className="h-3 w-3 mr-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-3 w-3 mr-1" />
                                Inactivo
                              </>
                            )}
                          </Badge>
                        </div>
                        <CardDescription>
                          {tour.tour_categories?.name} • {tour.location} • {tour.duration}
                        </CardDescription>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleToggleActive(tour)}
                        >
                          {tour.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleToggleFeatured(tour)}
                        >
                          <Star className={`h-4 w-4 ${tour.is_featured ? 'fill-current text-yellow-500' : ''}`} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(tour)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDelete(tour.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Capacidad</Label>
                        <p className="text-sm text-muted-foreground">{tour.max_capacity} personas</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Tipo de Precio</Label>
                        <p className="text-sm text-muted-foreground capitalize">{tour.pricing_type === 'individual' ? 'Individual' : 'Paquetes'}</p>
                      </div>
                      {tour.pricing_type === 'individual' ? (
                        <>
                          <div>
                            <Label className="text-sm font-medium">Precio Adulto</Label>
                            <p className="text-sm text-muted-foreground">${tour.adult_price}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Precio Niño</Label>
                            <p className="text-sm text-muted-foreground">${tour.child_price}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <Label className="text-sm font-medium">Precio Base</Label>
                            <p className="text-sm text-muted-foreground">${tour.base_price || 0}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Paquetes</Label>
                            <p className="text-sm text-muted-foreground">{tour.tour_packages?.length || 0} configurados</p>
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="text-sm font-medium">Slug</Label>
                        <p className="text-sm text-muted-foreground font-mono">{tour.slug}</p>
                      </div>
                    </div>
                    {tour.short_description && (
                      <div className="mt-4">
                        <Label className="text-sm font-medium">Descripción</Label>
                        <p className="text-sm text-muted-foreground mt-1">{tour.short_description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTours;